import React, { useCallback } from 'react';
import { Timer } from 'lucide-react';
import { PieChart, Pie, Cell } from 'recharts';

import ReportWidget from './components/ReportWidget';
import DataPoint from './components/DataPoint';
import { useTranslation } from 'react-i18next';

const COLORS = {
  onTime: '#10b981',
  doneOverdue: '#f59e0b',
  notDoneOverdue: '#ef4444',
  overall: '#3b82f6',
  empty: '#e5e7eb',
};

/**
 * Overall gauge - single color fill showing total on-time percentage
 */
const OverallGauge = ({ percentage, size = 220 }) => {
  const { t } = useTranslation();
  const filled = Math.max(0, Math.min(100, percentage));
  const empty = 100 - filled;

  const data = [
    { name: 'filled', value: filled, color: COLORS.overall },
    { name: 'empty', value: empty, color: COLORS.empty },
  ];

  const cx = size / 2;
  const cy = size / 2;
  const innerR = size * 0.32;
  const outerR = size * 0.48;
  const chartHeight = cy + 30;

  return (
    <div style={{ position: 'relative', width: size, height: chartHeight }}>
      <PieChart width={size} height={chartHeight}>
        <Pie
          data={data}
          cx={cx}
          cy={cy}
          startAngle={180}
          endAngle={0}
          innerRadius={innerR}
          outerRadius={outerR}
          paddingAngle={0}
          dataKey="value"
          stroke="none"
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
      </PieChart>
      <div style={{
        position: 'absolute',
        top: cy - innerR * 0.55,
        left: '50%',
        transform: 'translate(-50%)',
        textAlign: 'center',
        pointerEvents: 'none',
        width: innerR * 1.6,
      }}>
        <div style={{ fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.1, color: '#374151' }}>
          {percentage.toFixed(1)}<span style={{ fontSize: '0.85rem' }}>%</span>
        </div>
        <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
          {t('reporting.on_time_overdue.total_on_time', 'Total % On Time')}
        </div>
      </div>
    </div>
  );
};

/**
 * Category gauge showing on time / done overdue / not done overdue segments
 */
const CategoryGauge = ({ breakdown, label, size = 220, onSegmentClick }) => {
  const { t } = useTranslation();
  const data = [
    { name: 'ON_TIME', value: breakdown.onTimeCount, color: COLORS.onTime },
    { name: 'DONE_OVERDUE', value: breakdown.doneOverdueCount, color: COLORS.doneOverdue },
    { name: 'NOT_DONE_OVERDUE', value: breakdown.notDoneOverdueCount, color: COLORS.notDoneOverdue },
  ];
  const hasData = data.some(d => d.value > 0);

  const cx = size / 2;
  const cy = size / 2;
  const innerR = size * 0.32;
  const outerR = size * 0.48;
  const chartHeight = cy + 30;

  const chartData = hasData
    ? data.filter(d => d.value > 0)
    : [{ name: 'empty', value: 1, color: COLORS.empty }];

  return (
    <div className="text-center">
      <p className="mb-1 fw-medium" style={{ fontSize: '0.85rem' }}>{label}</p>
      <div style={{ position: 'relative', width: size, height: chartHeight }}>
        <PieChart width={size} height={chartHeight}>
          <Pie
            data={chartData}
            cx={cx}
            cy={cy}
            startAngle={180}
            endAngle={0}
            innerRadius={innerR}
            outerRadius={outerR}
            paddingAngle={hasData ? 1 : 0}
            dataKey="value"
            stroke="none"
            onClick={(entry) => {
              if (onSegmentClick && entry.name !== 'empty') {
                onSegmentClick(entry.name);
              }
            }}
            cursor={onSegmentClick ? 'pointer' : 'default'}
            label={({ cx: labelCx, cy: labelCy, midAngle, innerRadius: ir, outerRadius: or, value }) => {
              if (value === 0) return null;
              const RADIAN = Math.PI / 180;
              const radius = ir + (or - ir) * 0.5;
              const x = labelCx + radius * Math.cos(-midAngle * RADIAN);
              const y = labelCy + radius * Math.sin(-midAngle * RADIAN);
              return (
                <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>
                  {value}
                </text>
              );
            }}
            labelLine={false}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
        </PieChart>
        <div style={{
          position: 'absolute',
          top: cy - innerR * 0.55,
          left: '50%',
          transform: 'translate(-50%)',
          textAlign: 'center',
          pointerEvents: 'none',
          width: innerR * 1.6,
        }}>
          <div style={{ fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.1, color: '#374151' }}>
            {breakdown.onTimePercentage.toFixed(1)}<span style={{ fontSize: '0.85rem' }}>%</span>
          </div>
          <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
            {t('shared.on_time', 'On Time')}
          </div>
        </div>
      </div>
    </div>
  );
};

const OnTimeOverdueWidget = ({
  data,
  loading,
  error,
  onOnTimeClick,
  onOverdueClick,
  onSegmentClick,
  onDetailsClick,
}) => {
  const { t } = useTranslation();
  const { summary } = data || {};
  const {
    onTimeCount = 0,
    overdueCount = 0,
    totalOnTimePercentage = 0,
    nonRepeating,
    repeating,
    noDueDatePercentage = 0,
  } = summary || {};

  const isEmpty = !summary || (onTimeCount === 0 && overdueCount === 0);

  const handleNonRepeatingSegmentClick = useCallback((segmentName) => {
    if (onSegmentClick) {
      onSegmentClick(segmentName, false);
    }
  }, [onSegmentClick]);

  const handleRepeatingSegmentClick = useCallback((segmentName) => {
    if (onSegmentClick) {
      onSegmentClick(segmentName, true);
    }
  }, [onSegmentClick]);

  return (
    <ReportWidget
      title={t('reporting.on_time_overdue.title', 'On Time vs. Overdue')}
      loading={loading}
      onDetailsClick={onDetailsClick}
      error={error}
      isEmpty={isEmpty}
      emptyIcon={Timer}
      emptyMessage={t('reporting.on_time_overdue.empty', 'No work orders with due dates in the selected period.')}
    >
      {/* Two-column layout: left = counts + overall gauge, right = category gauges */}
      <div className="d-flex flex-wrap">
        {/* Left Column */}
        <div className="d-flex flex-column align-items-center border-end pe-4" style={{ minWidth: '240px' }}>
          {/* On Time / Overdue counts */}
          <div className="d-flex justify-content-center align-items-start gap-4 mb-2 py-2">
            <DataPoint
              value={onTimeCount}
              label={t('shared.on_time', 'On Time')}
              color={COLORS.onTime}
              onClick={onOnTimeClick}
            />
            <DataPoint
              value={overdueCount}
              label={t('shared.overdue', 'Overdue')}
              color={COLORS.notDoneOverdue}
              onClick={onOverdueClick}
            />
          </div>

          {/* Overall Gauge */}
          <OverallGauge percentage={totalOnTimePercentage} size={200} />
        </div>

        {/* Right Column - Category Gauges + Notice + Legend */}
        <div className="flex-grow-1 ps-4 pt-2">
          <div className="d-flex flex-wrap justify-content-center align-items-start gap-3">
            {nonRepeating && (
              <CategoryGauge
                breakdown={nonRepeating}
                label={t('reporting.on_time_overdue.non_repeating_on_time', 'Non-Repeating Work Orders On Time')}
                size={220}
                onSegmentClick={handleNonRepeatingSegmentClick}
              />
            )}
            {repeating && (
              <CategoryGauge
                breakdown={repeating}
                label={t('reporting.on_time_overdue.repeating_on_time', 'Repeating Work Orders On Time')}
                size={220}
                onSegmentClick={handleRepeatingSegmentClick}
              />
            )}
          </div>

          {/* Legend */}
          <div className="d-flex flex-wrap justify-content-center gap-3 mt-3">
            <div className="d-flex align-items-center gap-1">
              <span className="rounded-circle d-inline-block" style={{ width: 10, height: 10, backgroundColor: COLORS.onTime }} />
              <span className="text-muted" style={{ fontSize: '0.75rem' }}>{t('shared.on_time', 'On Time')}</span>
            </div>
            <div className="d-flex align-items-center gap-1">
              <span className="rounded-circle d-inline-block" style={{ width: 10, height: 10, backgroundColor: COLORS.doneOverdue }} />
              <span className="text-muted" style={{ fontSize: '0.75rem' }}>{t('shared.done_overdue', 'Done Overdue')}</span>
            </div>
            <div className="d-flex align-items-center gap-1">
              <span className="rounded-circle d-inline-block" style={{ width: 10, height: 10, backgroundColor: COLORS.notDoneOverdue }} />
              <span className="text-muted" style={{ fontSize: '0.75rem' }}>{t('shared.not_done_overdue', 'Not Done Overdue')}</span>
            </div>
          </div>

          {/* No Due Date Notice */}
          {noDueDatePercentage > 0 && (
            <div className="mt-2 pt-2 border-top">
              <p className="text-muted mb-0" style={{ fontSize: '0.8rem' }}>
                {t('reporting.on_time_overdue.no_due_date_notice', '{{percentage}}% of Created Non-Repeating Work Orders don\'t have a due date.', { percentage: noDueDatePercentage.toFixed(1) })}
              </p>
            </div>
          )}
        </div>
      </div>
    </ReportWidget>
  );
};

export default OnTimeOverdueWidget;
